/* ==========================================================================
   Animated search placeholder + clear button
   --------------------------------------------------------------------------
   Progressive enhancement over the theme's existing predictive search.
   Deliberately plain JS with no Alpine coupling: the header search input is
   owned by the `xModalSearch` Alpine component, so this file only ever
   touches `placeholder` and dispatches a real `input` event when clearing —
   which lets Alpine's own `handleChangeInput()` reset the results panel.

   Driven by data attributes emitted in snippets/header-search.liquid:
     data-animated-placeholder  JSON array of terms
     data-animated-prefix       string shown before each term
   ========================================================================== */
(function () {
  'use strict';

  // Timing of the typewriter cycle. Erasing stays faster than typing — reading
  // happens on the way in, so a slow rewind just feels sluggish — and HOLD_MS
  // does most of the work of making the whole thing feel unhurried.
  var TYPE_MS = 115;     // per character while typing
  var ERASE_MS = 55;     // per character while erasing
  var HOLD_MS = 2800;    // pause on a complete term
  var GAP_MS = 520;      // pause after erasing, before the next term

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function parseTerms(input) {
    try {
      var raw = JSON.parse(input.getAttribute('data-animated-placeholder') || '[]');
      return Array.isArray(raw) ? raw.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function animate(input) {
    var terms = parseTerms(input);
    if (!terms.length) return;

    // Shopify trims trailing whitespace from text settings, so the separating
    // space is added here rather than relying on the merchant typing one.
    var prefix = input.getAttribute('data-animated-prefix') || '';
    if (prefix && !/\s$/.test(prefix)) prefix += ' ';
    var fallback = input.getAttribute('data-static-placeholder') || '';
    var timer = null;
    var termIndex = 0;
    var charIndex = 0;
    var erasing = false;
    var paused = false;

    function stop() {
      if (timer) clearTimeout(timer);
      timer = null;
    }

    // While the shopper is typing or the field holds a value, the placeholder
    // is irrelevant — park it on something sensible and stop burning timers.
    function pause() {
      paused = true;
      stop();
      input.placeholder = fallback || prefix.replace(/\s+$/, '');
    }

    function resume() {
      if (!paused) return;
      paused = false;
      termIndex = 0;
      charIndex = 0;
      erasing = false;
      tick();
    }

    function tick() {
      if (paused) return;
      var term = terms[termIndex % terms.length];

      if (reduceMotion) {
        // No typing effect — just rotate the whole term.
        input.placeholder = prefix + term;
        termIndex++;
        timer = setTimeout(tick, HOLD_MS + GAP_MS);
        return;
      }

      if (!erasing) {
        charIndex++;
        input.placeholder = prefix + term.slice(0, charIndex);
        if (charIndex >= term.length) {
          erasing = true;
          timer = setTimeout(tick, HOLD_MS);
        } else {
          timer = setTimeout(tick, TYPE_MS);
        }
      } else {
        charIndex--;
        input.placeholder = prefix + term.slice(0, Math.max(charIndex, 0));
        if (charIndex <= 0) {
          erasing = false;
          termIndex++;
          timer = setTimeout(tick, GAP_MS);
        } else {
          timer = setTimeout(tick, ERASE_MS);
        }
      }
    }

    input.addEventListener('focus', pause);
    input.addEventListener('input', function () {
      if (input.value.length) pause();
    });
    input.addEventListener('blur', function () {
      if (!input.value.length) resume();
    });

    // Don't animate offscreen inputs (the header renders desktop + mobile
    // copies) and stop when the tab is hidden.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else if (!paused) {
        tick();
      }
    });

    if (input.value.length) {
      pause();
    } else {
      tick();
    }
  }

  function wireClearButton(input) {
    var form = input.closest('form');
    if (!form) return;
    var btn = form.querySelector('.search-clear-btn');
    if (!btn) return;

    function sync() {
      btn.hidden = input.value.length === 0;
    }

    input.addEventListener('input', sync);
    btn.addEventListener('click', function () {
      input.value = '';
      // A real event so the theme's Alpine handler clears the results panel.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      sync();
      input.focus();
    });

    sync();
  }

  function init() {
    var inputs = document.querySelectorAll('.input-search');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.dataset.animatedSearchReady) continue;
      input.dataset.animatedSearchReady = 'true';
      wireClearButton(input);
      if (input.hasAttribute('data-animated-placeholder')) animate(input);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The header can be re-rendered by the theme editor / Section Rendering API.
  document.addEventListener('shopify:section:load', init);
})();
